import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../utils/axiosInstance";

export const createFeature = createAsyncThunk(
  "createFeature/save",
  async (params: any) => {
    const response = await axiosInstance.post("/featsetup/feature", params);
    if (response.data.status.toLowerCase() === "success") {
      return response.data;
    } else if (response.data.status.toLowerCase() === "failure") {
      throw new Error(response.data.message || "Feature creation failed.");
    } else {
      throw new Error("Error submitting the request.");
    }
  },
);

export const editFeature = createAsyncThunk(
  "editFeature/save",
  async (params: any) => {
    const response = await axiosInstance.put("/featsetup/feature", params);
    if (response.data.status.toLowerCase() === "success") {
      return response.data;
    }
    if (response.data.status.toLowerCase() === "failure") {
      throw new Error(response.data.error);
    }
  },
);

export const deleteFeature = createAsyncThunk(
  "deleteFeature/remove",
  async (id: number) => {
    const response = await axiosInstance.delete("/featsetup/feature", {
      data: { id },
    });
    if (response.data.status.toLowerCase() === "success") {
      return response.data;
    }
    if (response.data.status.toLowerCase() === "failure") {
      throw new Error(response.data.error);
    }
  },
);

export type createFeatureState = {
  data: "";
  createStatus: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  editStatus: "idle" | "loading" | "succeeded" | "failed";
  editError: string | null;
  deleteStatus: "idle" | "loading" | "succeeded" | "failed";
  deleteError: string | null;
};

const initialState: createFeatureState = {
  data: "",
  createStatus: "idle",
  error: null,
  editStatus: "idle",
  editError: null,
  deleteStatus: "idle",
  deleteError: null,
};

const createFeatureSlice = createSlice({
  name: "createOrEditFeature",
  initialState,
  reducers: {
    resetCreateStatus: (state) => {
      state.createStatus = "idle";
      state.error = null;
    },
    resetEditStatus: (state) => {
      state.editStatus = "idle";
      state.editError = null;
    },
    resetDeleteStatus: (state) => {
      state.deleteStatus = "idle";
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createFeature.pending, (state) => {
        state.createStatus = "loading";
      })
      .addCase(createFeature.fulfilled, (state, action: PayloadAction<any>) => {
        state.createStatus = "succeeded";
        state.data = action.payload;
      })
      .addCase(createFeature.rejected, (state, action) => {
        state.createStatus = "failed";
        state.error = action.error.message || null;
      })
      .addCase(editFeature.pending, (state) => {
        state.editStatus = "loading";
      })
      .addCase(editFeature.fulfilled, (state, action: PayloadAction<any>) => {
        state.editStatus = "succeeded";
        state.data = action.payload;
      })
      .addCase(editFeature.rejected, (state, action) => {
        state.editStatus = "failed";
        state.editError = action.error.message || null;
      })
      .addCase(deleteFeature.pending, (state) => {
        state.deleteStatus = "loading";
      })
      .addCase(deleteFeature.fulfilled, (state, action: PayloadAction<any>) => {
        state.deleteStatus = "succeeded";
        state.data = action.payload;
      })
      .addCase(deleteFeature.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.deleteError = action.error.message || null;
      });
  },
});

export const { resetCreateStatus, resetEditStatus, resetDeleteStatus } =
  createFeatureSlice.actions;
export default createFeatureSlice.reducer;
